import React from 'react';
import { useTranslation } from 'react-i18next';

// Shared call-transcript view (Oct 16). Lifted verbatim from the Call History detail modal so
// the Calendar "Llamadas pendientes" detail (Llamada tab) renders the SAME bubbles the advisor
// already knows from Call History. Pure presentation: the caller fetches the call record.

export function normalizeTranscriptEntries(transcript) {
  if (!Array.isArray(transcript)) return [];
  const mapped = transcript.map((entry, index) => ({
    id: entry?.id || `${entry?.timestamp || ''}-${index}`,
    speaker: entry?.speaker || entry?.role || 'unknown',
    text: entry?.text || entry?.message || entry?.content || '',
    timestamp: entry?.timestamp || entry?.time || null,
    seq: entry?.seq != null ? entry.seq : index,
  }));
  // Sort by (timestamp, seq). When timestamps tie, user turns sort before assistant
  // turns as a role-bias fallback (speech_started precedes the agent response).
  mapped.sort((a, b) => {
    if (!a.timestamp && !b.timestamp) return a.seq - b.seq;
    if (!a.timestamp) return 1;
    if (!b.timestamp) return -1;
    if (a.timestamp !== b.timestamp) return a.timestamp < b.timestamp ? -1 : 1;
    if (a.seq !== b.seq) return a.seq - b.seq;
    // role-bias: user before assistant on exact tie
    const roleBias = s => (s === 'user' ? 0 : 1);
    return roleBias(a.speaker) - roleBias(b.speaker);
  });
  return mapped;
}

export const isCustomerSpeaker = (speaker) => ['user', 'customer', 'caller', 'cliente', 'human'].includes(String(speaker || '').toLowerCase());

export function toDisplayText(value, fallback = '—') {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => toDisplayText(item, '')).filter(Boolean).join(', ') || fallback;
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return fallback;
}

export function formatDateTime(value) {
  if (!value) return '—';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleString();
}

/**
 * transcript: raw entries from /api/calls/{sid}/detail (or any call record).
 * agentName: label for the assistant side; emptyText overrides the empty-state copy.
 */
export default function CallTranscript({ transcript, agentName = '', emptyText = '', maxHeightClass = 'max-h-[360px]', testId = 'call-transcript' }) {
  const { t } = useTranslation();
  const entries = normalizeTranscriptEntries(transcript);
  if (entries.length === 0) {
    return <div className="px-4 py-8 text-center text-sm text-muted" role="status" data-testid={testId + '-empty'}>
      {emptyText || t('calls.noTranscript', 'No transcript available for this call.')}
    </div>;
  }
  return (
    <div className={`${maxHeightClass} overflow-y-auto p-4 space-y-3 bg-background/20`} data-testid={testId}>
      {entries.map((entry) => {
        const isCustomer = isCustomerSpeaker(entry.speaker);
        return (
          <div
            key={entry.id}
            className={`rounded-xl border-l-4 p-3 ${
              isCustomer
                ? 'border-blue-400 bg-blue-50 dark:bg-blue-950/30'
                : 'border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30'
            }`}
          >
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="font-semibold text-foreground">
                {isCustomer ? t('calls.customer', 'Customer') : toDisplayText(agentName, t('calls.agent', 'Agent'))}
              </span>
              <span className="text-muted">{formatDateTime(entry.timestamp)}</span>
            </div>
            <p className="text-sm text-foreground whitespace-pre-wrap">{toDisplayText(entry.text, '')}</p>
          </div>
        );
      })}
    </div>
  );
}
